import { Link } from 'react-router-dom';
import { TimelinesWrapper } from '@/partials/timelines/default/item';
import { renderMultiAvatars } from '@/utils';
import { toTitleCase } from '@/utils/String';
import { ClockFading } from 'lucide-react';

const containsHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const truncate = (text, maxLength = 100) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

const SENSITIVE_KEYS = new Set(['html', 'design', 'signature']);
const SKIP_KEYS = new Set([
    'created_at',
    'updated_at',
    'deleted_at',
    'uuid',
    'id',
    'user_id',
    'tenant_id',
    'crm_template_id',
    'created_by',
    'updated_by',
    'approved_by',
    'contact_id',
    'matter_id',
    'invoice_template_id',
]);

const shouldSkipKey = (key, tableName = null) => {
    if (!key) return true;
    if (SKIP_KEYS.has(key)) return true;
    if (tableName === 'intake_forms' && key === 'fields') return true;
    if (/_id$/.test(key)) return true;
    if (key === 'uuid' || key === 'id') return true;
    return false;
};

const formatValue = (val) => {
    if (val === null || val === '' || val === undefined || val === 'null') return 'N/A';

    if (val === 1) return 'Yes';
    if (val === 0) return 'No';

    const valueStr = String(val);

    if (containsHtml(valueStr)) {
        return truncate(stripHtml(valueStr));
    }

    return valueStr;
};

const normalizeCompareValue = (val) => {
    if (val === null || val === undefined || val === '' || val === 'null') return null;

    if (val === true || val === false) return val;
    if (val === 1 || val === '1') return true;
    if (val === 0 || val === '0') return false;

    if (typeof val === 'string') {
        const lower = val.trim().toLowerCase();
        if (lower === 'true' || lower === 'yes') return true;
        if (lower === 'false' || lower === 'no') return false;
        if (lower === 'null' || lower === 'n/a') return null;
        if (/^-?\d+(\.\d+)?$/.test(lower)) return Number(lower);
        return lower;
    }

    if (typeof val === 'number') return val;
    return val;
};

const getActionLabel = (type) => {
    if (type === 'edit') return 'updated';
    if (type === 'delete') return 'deleted';
    if (type === 'create') return 'created';
    if (type === 'stage') return 'stage changed';
    return 'updated';
};

const TABLE_LABELS = {
    crm_subscriptions: 'Subscriptions',
    crm_templates: 'Email Templates',
    matters: 'Matters',
    invoices: 'Invoices',
};

const getTableLabel = (tableName) => {
    if (!tableName) return 'Record';
    if (TABLE_LABELS[tableName]) return TABLE_LABELS[tableName];
    return toTitleCase(String(tableName).replace(/_/g, ' '));
};

const getPrimaryTitle = (data, tableName) => {
    if (!data || typeof data !== 'object') return null;
    if (tableName === 'referral_contacts' && data.p_name) return `Contact "${data.p_name}"`;
    if (tableName === 'contacts' && data.fullName) return `Contact "${data.fullName}"`;
    if (tableName === 'matters' && data.name) return `Matter "${data.name}"`;
    if (tableName === 'invoices' && data.number) return `Invoice #${data.number}`;
    if (tableName === 'intake_forms' && (data.name || data.title)) {
        return `Intake form "${data.name || data.title}"`;
    }
    return data.name || data.title || data.subject || null;
};

const buildActivityTitle = ({ activity, primaryTitle, action, tableLabel }) => {
    const tableName = activity?.table_name;

    // Stage changes come from the matter's stage history (OL-209).
    const stage = activity?.stage_change;
    if (stage) {
        const subject = primaryTitle || 'Matter';
        const to = stage.to || 'no stage';
        return stage.from ? `${subject} moved from ${stage.from} to ${to}` : `${subject} stage set to ${to}`;
    }

    if (tableName === 'contacts' && !primaryTitle) {
        return `Contact was ${action}`;
    }

    if (tableName === 'contact_addresses' || tableName === 'contact_address' || tableName === 'addresses') {
        if (activity?.log_type === 'create') return 'Address added in the contact form';
        if (activity?.log_type === 'edit') return 'Address updated in the contact form';
    }

    if (primaryTitle) {
        return `${primaryTitle} was ${action}`;
    }

    return `A ${tableLabel} record was ${action}`;
};

const buildSummaryFields = (data, tableName = null, limit = 4) => {
    if (!data || typeof data !== 'object') return [];

    if (tableName === 'intake_forms') {
        if (data.status === null || data.status === undefined || data.status === '') return [];
        return [{
            key: 'Status',
            value: formatValue(data.status),
        }];
    }

    if (tableName === 'esign_documents' && data.document_name) {
        return [{
            key: 'Document Name',
            value: formatValue(data.document_name),
        }];
    }

    return Object.entries(data)
        .filter(([key, val]) => !SENSITIVE_KEYS.has(key) && !shouldSkipKey(key, tableName) && val !== null && val !== undefined && val !== '')
        .map(([key, val]) => ({
            key: toTitleCase(key.replace(/_/g, ' ')),
            value: formatValue(val),
        }))
        .slice(0, limit);
};

const RecentActivityLog = ({ activityData }) => {
    if (!activityData || activityData.length === 0) {
        return (
            <div className="text-sm text-gray-600 italic">
                No recent activity found.
            </div>
        );
    }

    return (
        <>
            {activityData.map((activity, index) => {
                const userFullName = `${activity?.user?.first_name || ''} ${activity?.user?.last_name || ''}`.trim() || 'Unknown user';
                const action = getActionLabel(activity?.log_type);
                const table = getTableLabel(activity?.table_name);
                const invoiceNumber =
                    activity?.table_name === 'invoices'
                        ? activity?.data?.number || activity?.current_data?.number
                        : null;

                const oldData = activity?.data || {};
                const newData = activity?.current_data || {};
                const primaryTitle = getPrimaryTitle(newData, activity?.table_name) || getPrimaryTitle(oldData, activity?.table_name);
                const summaryFields = activity?.stage_change ? [] : buildSummaryFields(
                    activity?.log_type === 'edit' ? newData : oldData,
                    activity?.table_name
                );
                const actionTone =
                    activity?.log_type === 'create'
                        ? 'bg-success-light border-l-success-active'
                        : activity?.log_type === 'delete'
                            ? 'bg-danger-light border-l-danger-active'
                            : 'bg-primary-light border-l-primary-active';

                const changedFields = [];

                if (activity?.log_type === 'edit') {
                    if (activity?.table_name === 'intake_forms') {
                        const oldVal = oldData?.status;
                        const newVal = newData?.status;
                        const normalizedOld = normalizeCompareValue(oldVal);
                        const normalizedNew = normalizeCompareValue(newVal);

                        if (normalizedOld !== normalizedNew) {
                            changedFields.push({
                                key: 'Status',
                                from: formatValue(oldVal),
                                to: formatValue(newVal),
                            });
                        }
                    } else {
                        Object.keys(newData).forEach((key) => {
                            if (shouldSkipKey(key, activity?.table_name) || SENSITIVE_KEYS.has(key)) return;
                            if (activity?.table_name === 'esign_documents' && key === 'values') return;

                            const oldVal = oldData[key];
                            const newVal = newData[key];

                            // E-sign documents store field placements as a large `coordinates`
                            // JSON blob — don't dump the raw diff, just note that it changed.
                            if (activity?.table_name === 'esign_documents' && key === 'coordinates') {
                                if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                                    changedFields.push({
                                        key: 'Coordinates',
                                        message: 'Coordinates updated',
                                    });
                                }
                                return;
                            }

                            const normalizedOld = normalizeCompareValue(oldVal);
                            const normalizedNew = normalizeCompareValue(newVal);

                            if (normalizedOld !== normalizedNew) {
                                const displayOld = formatValue(oldVal);
                                const displayNew = formatValue(newVal);

                                changedFields.push({
                                    key: toTitleCase(key.replace(/_/g, ' ')),
                                    from: displayOld,
                                    to: displayNew,
                                });
                            }
                        });
                    }
                }

                const avatarNode = (
                    <div className="">
                        {renderMultiAvatars(activity?.user)}
                    </div>
                );

                return (
                    <TimelinesWrapper icon="share" iconNode={avatarNode} line={true} key={index}>
                        <div className={`flex flex-col gap-3 mb-2 rounded-md border border-gray-100 border-l-4 p-3 shadow-[0_1px_0_rgba(0,0,0,0.04)] ${actionTone}`}>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-900 font-medium truncate">
                                        {userFullName}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                        {activity?.user?.email || 'No email'}
                                    </div>
                                </div>
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    {toTitleCase(action)}
                                </div>
                            </div>

                            <div className="text-sm">
                                {buildActivityTitle({
                                    activity,
                                    primaryTitle,
                                    action,
                                    tableLabel: table,
                                })}

                                {changedFields.length > 0 && (
                                    <div className="mt-2 space-y-1 rounded-md bg-card border border-gray-100 p-2">
                                        {changedFields.map((field, idx) => (
                                            <div key={idx} className="text-xs">
                                                {field.message ? (
                                                    <span className="text-gray-800 dark:text-gray-100">{field.message}</span>
                                                ) : (
                                                    <>
                                                        <span className="font-semibold">{field.key}:</span>{' '}
                                                        changed from{' '}
                                                        <span className="line-through text-gray-500 capitalize">{field.from}</span>{' '}
                                                        to{' '}
                                                        <span className="text-gray-800 dark:text-gray-100">{field.to}</span>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!changedFields.length && summaryFields.length > 0 && (
                                    <div className="mt-2 grid grid-cols-1 gap-1 rounded-md bg-card border border-gray-100 p-2">
                                        {summaryFields.map((field, idx) => (
                                            <div key={idx} className="text-xs">
                                                <span className="font-semibold">{field.key}:</span> {field.value}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                                <span className='flex items-center gap-2'>
                                    <ClockFading className='size-4' />
                                    {activity?.humanize_datetime || 'No timestamp available'}
                                </span>
                            </div>
                        </div>
                    </TimelinesWrapper>
                );
            })}
        </>
    );
};

export { RecentActivityLog };
